import React, { useState } from 'react';
import { X, Sparkles, Volume2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { templates } from '../mock';
import InvitationExperience from '../components/InvitationExperience';
import { useTranslation } from '../LanguageContext';

function TemplateCard({ tpl, onOpen }) {
  return (
    <div className="flex flex-col items-center fade-up">
      <div className="card-poster group" onClick={() => onOpen(tpl)}>
        <div className="inner">
          <div className="style-badge">{tpl.category}</div>
          <img src={tpl.image} alt={tpl.name} loading="lazy" className="transition-transform duration-[6s] ease-out group-hover:scale-110" />
          {/* Live indicator */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm z-[3]">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-[9px] text-white tracking-widest font-medium">LIVE</span>
          </div>
          {/* Music indicator */}
          <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm z-[3]">
            <Volume2 size={10} className="text-white/90" />
            <span className="text-[9px] text-white/90 uppercase tracking-widest">{tpl.music}</span>
          </div>
          <div className="play-btn opacity-80 group-hover:opacity-100 group-hover:scale-110">
            <Sparkles size={16} className="text-[color:var(--burgundy)]" />
          </div>
        </div>
      </div>
      <div className="text-center mt-4 font-serif text-[1.05rem] text-[color:var(--ink)]">{tpl.name}</div>
      <div className="text-xs text-[color:var(--ink-soft)] italic">{tpl.age}</div>
    </div>
  );
}

function PreviewModal({ template, onClose }) {
  if (!template) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 text-[#f8ede0] p-2 z-10"><X size={24} /></button>
        <InvitationExperience data={template} initiallyOpen={true} autoScroll={true} />
        <div className="text-center mt-5 text-[#f8ede0] font-serif">
          <div className="text-xl">{template.name}</div>
          <div className="text-sm italic opacity-70 mt-1">{template.category} · {template.age}</div>
        </div>
      </div>
    </div>
  );
}

export default function TemplatesPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('Tutti');

  const groups = {
    'Tutti': templates,
    'Bambini 1-2 anni': templates.filter((t) => ['1 anno', '2 anni'].includes(t.category)),
    'Bambini 3-5 anni': templates.filter((t) => t.category === '3-5 anni'),
    'Bambini 6-9 anni': templates.filter((t) => t.category === '6-9 anni'),
    'Ragazzi 10-13 anni': templates.filter((t) => t.category === '10-13 anni'),
    'Adolescenti 14-16 anni': templates.filter((t) => ['14-16 anni', '16 anni'].includes(t.category)),
    'Laurea': templates.filter((t) => t.category === 'Laurea'),
    '18 anni': templates.filter((t) => t.category === '18 anni'),
    'Matrimonio': templates.filter((t) => t.category === 'Matrimonio'),
    'Battesimo & Comunione': templates.filter((t) => ['Battesimo', 'Comunione'].includes(t.category)),
    'Gender Reveal': templates.filter((t) => t.category === 'Gender Reveal'),
    'Anniversario': templates.filter((t) => t.category === 'Anniversario'),
    'Aziendale & B&B': templates.filter((t) => ['Aziendale', 'Ospitalità'].includes(t.category)),
  };
  const list = groups[filter] || templates;

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
      <div className="text-center mb-12">
        <div className="eyebrow mb-5">La galleria</div>
        <h1 className="font-serif text-[2.6rem] md:text-[4rem] leading-[1.05] mb-5">
          Trenta template, <span className="italic-serif">una tua storia</span>.
        </h1>
        <p className="text-[color:var(--ink-soft)] max-w-2xl mx-auto text-lg">
          Ogni template è già animato e pronto. Tocca per vederlo dal vivo con musica.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-12">
        {Object.keys(groups).map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`chip ${filter === c ? '!bg-[color:var(--burgundy)] !text-[#f8ede0] !border-[color:var(--burgundy)]' : ''}`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
        {list.map((tpl) => <TemplateCard key={tpl.id} tpl={tpl} onOpen={setSelected} />)}
      </div>

      <div className="text-center mt-16">
        <div className="mb-5 text-[color:var(--ink-soft)] flex items-center justify-center gap-2">
          <Sparkles size={16} className="text-[color:var(--burgundy)]" />
          Ti piace un template? Personalizzalo in 2 minuti.
        </div>
        <Link to="/crea" className="btn-primary">{t('nav.builder')}</Link>
      </div>

      <PreviewModal template={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
